import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { AdminAuthProvider, useAdminAuth } from './lib/adminAuth';
import { AuthProvider, useAuth } from './lib/auth';
import { About } from './pages/About';
import { Architecture } from './pages/Architecture';
import { Commands } from './pages/Commands';
import { Contact } from './pages/Contact';
import { Docs } from './pages/Docs';
import { Features } from './pages/Features';
import { Home } from './pages/Home';
import { HowItWorks } from './pages/HowItWorks';
import { NotFound } from './pages/NotFound';
import { Pricing } from './pages/Pricing';
import { SecurityCompliance } from './pages/SecurityCompliance';
import { Support } from './pages/Support';
import { Privacy } from './pages/legal/Privacy';
import { Refunds } from './pages/legal/Refunds';
import { Terms } from './pages/legal/Terms';
import { Activate } from './pages/auth/Activate';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ResetPassword } from './pages/auth/ResetPassword';
import { Credentials } from './pages/app/Credentials';
import { DashboardLayout } from './pages/app/DashboardLayout';
import { Overview } from './pages/app/Overview';
import { Profile } from './pages/app/Profile';
import { Subscription } from './pages/app/Subscription';
import { Templates } from './pages/app/Templates';
import { Usage } from './pages/app/Usage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminLogin } from './pages/admin/AdminLogin';
import { AdminDashboard } from './pages/admin/Dashboard';
import { AdminPricingPlans } from './pages/admin/PricingPlans';
import { AdminPromotions } from './pages/admin/Promotions';
import { AdminRevenue } from './pages/admin/Revenue';
import { AdminSettings } from './pages/admin/AdminSettings';
import { AdminSubscriptions } from './pages/admin/Subscriptions';
import { AdminUsers } from './pages/admin/Users';

/**
 * Restores the top of the page on navigation, as a full page load would —
 * unless the URL names a section, in which case that section is what the
 * visitor asked for.
 *
 * Without the hash branch this effect actively breaks every anchor link on the
 * site: the browser scrolls to the element, then this runs and yanks the page
 * back to the top. `/docs#security` in the footer depends on it, and it had
 * been quietly broken for as long as both existed.
 *
 * The lookup is deferred to the next frame because this fires on the render
 * that *begins* showing the new route; the target element does not exist yet
 * when the effect first runs. A missing id falls back to the top rather than
 * leaving the visitor wherever the previous page happened to be scrolled.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const frame = requestAnimationFrame(() => {
      // `decodeURIComponent` because a hash with non-ASCII characters arrives
      // percent-encoded and would never match the element's id.
      const id = decodeURIComponent(hash.slice(1));
      const target = id ? document.getElementById(id) : null;

      if (target) {
        target.scrollIntoView({ block: 'start' });
      } else {
        window.scrollTo(0, 0);
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return null;
}

/**
 * Gate for signed-in routes.
 *
 * Waits for the initial silent refresh before deciding, so a signed-in user
 * reloading the page is not bounced to the login screen for the split second
 * before their session is restored.
 *
 * This is a convenience, not a security control: the API authorises every
 * request on its own, and a user who edits their way past this guard reaches
 * nothing but 401s.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner label="Restoring your session" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}


/**
 * Gate for the admin portal.
 *
 * Its own guard and its own context, not a `role` check inside `RequireAuth`.
 * The two session kinds are separate all the way down - separate tables,
 * separate tokens, separate refresh cookies - and a shared guard would be the
 * one place they met, which is the one place a mistake would matter.
 *
 * Like `RequireAuth` this is a convenience, not a security control: the API
 * authorises every request on its own, and a visitor who edits their way past
 * this reaches nothing but 401s.
 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { admin, loading } = useAdminAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-hull-950">
        <Spinner label="Restoring your session" />
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <ScrollToTop />
        <Routes>
          {/* --- Admin portal ---------------------------------------------
              Mounted outside <Layout> on purpose. An operator suspending an
              account should not be looking at a "Start free trial" button, and
              the public header would put one there. */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="plans" element={<AdminPricingPlans />} />
            <Route path="promotions" element={<AdminPromotions />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route element={<Layout />}>
            {/* --- Public ------------------------------------------------- */}
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/architecture" element={<Architecture />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/support" element={<Support />} />
            <Route path="/docs" element={<Docs />} />
            {/* Nested under /docs as its own page rather than a section of it:
                it is a reference of a few hundred controls, and folding that into
                the getting-started page would bury the four commands someone
                arrives looking for. */}
            <Route path="/docs/security-compliance" element={<SecurityCompliance />} />
            {/* Getting started shows the commands that take someone from an empty
                machine to a first report. This is the other read: the reader who
                already has a report and needs the command they were never given a
                reason to meet. */}
            <Route path="/docs/commands" element={<Commands />} />

            {/* --- Legal ---------------------------------------------------
                Paddle will not approve a checkout on this domain unless these
                three are live and reachable from site navigation, so they are
                routed at the top level rather than nested under /legal. */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund-policy" element={<Refunds />} />
            <Route path="/privacy" element={<Privacy />} />

            {/* --- Authentication ----------------------------------------- */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* --- Dashboard (mounted here rather than as a second app) ---- */}
            <Route
              path="/app"
              element={
                <RequireAuth>
                  <DashboardLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Overview />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="credentials" element={<Credentials />} />
              <Route path="templates" element={<Templates />} />
              <Route path="usage" element={<Usage />} />
              <Route path="profile" element={<Profile />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </AdminAuthProvider>
    </AuthProvider>
  );
}
